import * as React from "react"
import { instantBookingAreaAllowed } from "../../../../supabase/functions/_shared/delivery-coverage.ts"
import { MAX_ONLINE_BOOKING_QUANTITY } from "../../../../supabase/functions/_shared/booking-quantity.ts"
import { londonDateOnly } from "../../../../supabase/functions/_shared/calendar-dates.ts"
import type { BookingUrlStep } from "../bookingHistory"
import { PRODUCT_CATALOG, productMeta } from "../catalogue"
import { calendarPostcode, derivedDates, inferRangeChoice, rangeError, serviceRangeError } from "../dateSelection"
import { localDate } from "../format"
import type { Availability, HireBookingProps } from "../types"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "error" | "postcode" | "settledPostcode" | "activeSlug" | "quantity" | "restoredCheckoutPhase" | "calendarPhase" | "calendarSnapshot" | "draftRestored" | "restoredCheckoutStatus" | "initialDraftRef" | "clientSecret" | "confirmationToken" | "quote" | "range" | "phase" | "product" | "activeDateField" | "availability" | "selectedAddonIds" | "journeyStep" | "bookingPane">
    & { props: HireBookingProps }

export function useCheckoutModel(inputs: Inputs) {
    const {
        props,
        error,
        postcode,
        settledPostcode,
        activeSlug,
        quantity,
        restoredCheckoutPhase,
        calendarPhase,
        calendarSnapshot,
        draftRestored,
        restoredCheckoutStatus,
        initialDraftRef,
        clientSecret,
        confirmationToken,
        quote,
        range,
        phase,
        product,
        activeDateField,
        availability,
        selectedAddonIds,
        journeyStep,
        bookingPane,
    } = inputs
    const baseUrl = props.apiBaseUrl.replace(/\/$/, "")
    const normalizedPostcode = calendarPostcode(postcode)
    const deliveryContactRequired = Boolean(normalizedPostcode) && !instantBookingAreaAllowed(
        product?.productGroup ?? PRODUCT_CATALOG.find(item => item.slug === activeSlug)?.type,
        normalizedPostcode.split(" ")[0],
    )
    const requestedPostcode = calendarPostcode(settledPostcode)
    const calendarContext = `${activeSlug}:${quantity}:${requestedPostcode}`
    const calendarContextRef = React.useRef(calendarContext)
    calendarContextRef.current = calendarContext
    const calendarReady = !deliveryContactRequired && Boolean(normalizedPostcode) && restoredCheckoutPhase === "ready" && calendarPhase === "ready" && calendarSnapshot?.productSlug === activeSlug
        && calendarSnapshot.quantity === quantity && calendarSnapshot.postcode === normalizedPostcode
    const calendarRefreshing = !deliveryContactRequired && !calendarReady && calendarSnapshot?.productSlug === activeSlug
        && restoredCheckoutPhase === "ready" && calendarPhase !== "error"
    // Notice is checked when checkout is created. Its existing payment window survives a cutoff.
    const savedCheckout = !draftRestored && restoredCheckoutStatus === "payment_pending" ? initialDraftRef.current : null
    const noticeCheckoutProtected = Boolean((clientSecret && confirmationToken && quote && Date.parse(quote.expiresAt) > Date.now())
        || (savedCheckout?.checkout && savedCheckout.quote && Date.parse(savedCheckout.quote.expiresAt) > Date.now()))
    const calendarRangeError = calendarReady ? serviceRangeError(range, calendarSnapshot, noticeCheckoutProtected || phase === "initialising") : ""
    const choice = React.useMemo(() => inferRangeChoice(range, product), [range, product])
    const unavailableDates = React.useMemo(() => calendarSnapshot?.unavailableDates.map(localDate) ?? [], [calendarSnapshot])
    const unavailableServiceDates = React.useMemo(() => (activeDateField === "start"
        ? calendarSnapshot?.deliveryUnavailableDates
        : calendarSnapshot?.collectionUnavailableDates)?.map(localDate) ?? [], [activeDateField, calendarSnapshot])
    const selection = choice?.selection ?? null
    // Keep the quantity limit steady during a quantity check.
    // Cached results only inform presentation, never checkout readiness.
    const availabilityContext = JSON.stringify([activeSlug, selection, postcode])
    const lastAvailability = React.useRef<{ context: string; result: Availability } | null>(null)
    React.useEffect(() => {
        if (availability?.available) {
            lastAvailability.current = { context: availabilityContext, result: availability }
        } else if (availability || error || lastAvailability.current?.context !== availabilityContext) {
            lastAvailability.current = null
        }
    }, [availability, availabilityContext, error])
    const displayedAvailability = availability
        ?? (!error && lastAvailability.current?.context === availabilityContext ? lastAvailability.current.result : null)
    // Delivery pricing belongs to the product/postcode, not the selected dates.
    // Retain it through date edits and failed date checks without reusing their
    // availability result to enable checkout. A new quote/check can update it.
    const deliveryContext = normalizedPostcode ? JSON.stringify([activeSlug, normalizedPostcode]) : ""
    const quotedDeliveryFeePence = quote?.product.slug === activeSlug && calendarPostcode(quote.postcode) === normalizedPostcode
        ? quote.lineItems.find(item => item.key === "delivery")?.amountPence
        : undefined
    const checkedDeliveryFeePence = deliveryContext && product?.slug === activeSlug
        ? quotedDeliveryFeePence ?? (availability?.available ? availability.deliveryFeePence : undefined)
        : undefined
    const lastDeliveryFee = React.useRef<{ context: string; amountPence: number } | null>(null)
    React.useEffect(() => {
        if (checkedDeliveryFeePence !== undefined) {
            lastDeliveryFee.current = { context: deliveryContext, amountPence: checkedDeliveryFeePence }
        } else if (lastDeliveryFee.current?.context !== deliveryContext) {
            lastDeliveryFee.current = null
        }
    }, [checkedDeliveryFeePence, deliveryContext])
    // The postcode calendar already knows the delivery tariff before dates are
    // selected. Prefer a quote/range check (including its retained fee) when present.
    const calendarDeliveryFeePence = calendarSnapshot?.productSlug === activeSlug && calendarSnapshot.postcode === normalizedPostcode
        && typeof calendarSnapshot.deliveryFeePence === "number" && Number.isInteger(calendarSnapshot.deliveryFeePence) && calendarSnapshot.deliveryFeePence >= 0
        ? calendarSnapshot.deliveryFeePence : undefined
    const deliveryFeePence = checkedDeliveryFeePence
        ?? (lastDeliveryFee.current?.context === deliveryContext ? lastDeliveryFee.current.amountPence : undefined)
        ?? (deliveryContext ? calendarDeliveryFeePence : undefined)
    const previewDates = React.useMemo(() => derivedDates(choice), [choice])
    const dateMessage = rangeError(range, product)
    const today = React.useMemo(() => localDate(calendarSnapshot?.noticeDate ?? londonDateOnly()), [calendarSnapshot?.noticeDate])
    const firstMonth = React.useMemo(() => {
        // September 2026 has closed for bookings; open directly on October.
        const skipSeptember = today.getFullYear() === 2026 && today.getMonth() === 8
        return new Date(today.getFullYear(), today.getMonth() + (skipSeptember ? 1 : 0), 1)
    }, [today])
    const lastMonth = React.useMemo(() => {
        const daysInLastMonth = new Date(today.getFullYear(), today.getMonth() + 14, 0).getDate()
        return new Date(today.getFullYear(), today.getMonth() + 13, Math.min(today.getDate(), daysInLastMonth))
    }, [today])
    const meta = productMeta(activeSlug || PRODUCT_CATALOG[0].slug)
    const stockCap = Math.max(1, Math.min(MAX_ONLINE_BOOKING_QUANTITY, product?.maximumQuantity ?? 1, displayedAvailability?.availableQuantity ?? MAX_ONLINE_BOOKING_QUANTITY))
    const selectedAddons = React.useMemo(
        () => product?.accessories.filter((addon) => selectedAddonIds.includes(addon.id)) ?? [],
        [product, selectedAddonIds],
    )
    const addonTotalPence = React.useMemo(
        () => selectedAddons.reduce((sum, addon) => sum + addon.pricePence, 0),
        [selectedAddons],
    )

    const workflowStep: BookingUrlStep = journeyStep === "booking" ? bookingPane === "addons" ? "addons" : "dates" : journeyStep
    const checkoutResumable = () => !deliveryContactRequired && Boolean(quote && clientSecret && confirmationToken && new Date(quote.expiresAt).getTime() > Date.now())

    return {
        baseUrl,
        normalizedPostcode,
        deliveryContactRequired,
        requestedPostcode,
        calendarContext,
        calendarContextRef,
        calendarReady,
        calendarRefreshing,
        calendarRangeError,
        deliveryFeePence,
        choice,
        unavailableDates,
        unavailableServiceDates,
        selection,
        previewDates,
        dateMessage,
        today,
        firstMonth,
        lastMonth,
        meta,
        stockCap,
        selectedAddons,
        addonTotalPence,
        workflowStep,
        checkoutResumable,
    }
}
